/**
 * IndexedDB Blob Storage for crash-safe video/audio persistence
 * 
 * Blobs are saved immediately after recording and only deleted
 * after successful upload AND database save confirmation.
 */

import { openDB, DBSchema, IDBPDatabase } from 'idb';

export interface PendingUpload {
  id: string;
  questionId: string;
  testResponseId: string;
  videoBlob?: Blob;
  audioBlob?: Blob;
  videoMimeType?: string;
  audioMimeType?: string;
  createdAt: number;
  status: 'pending' | 'uploading' | 'uploaded' | 'saved';
  // Paths are set after TUS upload completes - persisted for crash recovery
  videoPath?: string;
  audioPath?: string;
}

interface BlobStorageDB extends DBSchema {
  pendingUploads: {
    key: string;
    value: PendingUpload;
    indexes: {
      'by-question': string;
      'by-test-response': string;
      'by-status': string;
    };
  };
}

const DB_NAME = 'testera-blob-storage';
const DB_VERSION = 2; // Bumped for videoPath/audioPath fields

let dbPromise: Promise<IDBPDatabase<BlobStorageDB>> | null = null;

async function getDB(): Promise<IDBPDatabase<BlobStorageDB>> {
  if (!dbPromise) {
    dbPromise = openDB<BlobStorageDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        // Only create object store if upgrading from version 0 (fresh install)
        if (oldVersion < 1) {
          const store = db.createObjectStore('pendingUploads', { keyPath: 'id' });
          store.createIndex('by-question', 'questionId');
          store.createIndex('by-test-response', 'testResponseId');
          store.createIndex('by-status', 'status');
        }
        
        // Version 2 adds videoPath and audioPath fields
        // No schema migration needed - these are just new optional fields
        if (oldVersion < 2) {
          console.log('[BlobStorage] Upgraded to version 2 - videoPath/audioPath support');
        }
      },
    });
  }
  return dbPromise;
}

/**
 * Save blobs to IndexedDB immediately after recording
 * This is the FIRST thing to do after recording stops - before any upload attempt
 */
export async function saveBlobsToIndexedDB(
  questionId: string,
  testResponseId: string,
  videoBlob?: Blob,
  audioBlob?: Blob,
  videoMimeType?: string,
  audioMimeType?: string
): Promise<string> {
  const db = await getDB();
  const id = crypto.randomUUID();
  
  const record: PendingUpload = {
    id,
    questionId,
    testResponseId,
    videoBlob,
    audioBlob,
    videoMimeType,
    audioMimeType,
    createdAt: Date.now(),
    status: 'pending',
  };
  
  await db.put('pendingUploads', record);
  console.log(`[BlobStorage] Saved blobs for question ${questionId}, id: ${id}`);
  
  return id;
}

/**
 * Save blobs to IndexedDB with a pre-generated ID
 * Used to prevent race conditions in fast upload scenarios
 */
export async function saveBlobsToIndexedDBWithId(
  id: string,
  questionId: string,
  testResponseId: string,
  videoBlob?: Blob,
  audioBlob?: Blob,
  videoMimeType?: string,
  audioMimeType?: string
): Promise<string> {
  const db = await getDB();

  const record: PendingUpload = {
    id,
    questionId,
    testResponseId,
    videoBlob,
    audioBlob,
    videoMimeType,
    audioMimeType,
    createdAt: Date.now(),
    status: 'pending',
  };

  await db.put('pendingUploads', record);
  console.log(`[BlobStorage] Saved blobs with pre-generated ID for question ${questionId}, id: ${id}`);

  return id;
}

/**
 * Update the status of a pending upload
 */
export async function updateBlobStatus(
  id: string,
  status: PendingUpload['status']
): Promise<void> {
  const db = await getDB();
  const record = await db.get('pendingUploads', id);
  
  if (record) {
    record.status = status;
    await db.put('pendingUploads', record);
    console.log(`[BlobStorage] Updated status for ${id}: ${status}`);
  }
}

/**
 * Update the uploaded paths for a pending upload
 * Called after TUS upload completes successfully
 */
export async function updateBlobPaths(
  id: string,
  videoPath?: string,
  audioPath?: string
): Promise<void> {
  const db = await getDB();
  const record = await db.get('pendingUploads', id);

  if (record) {
    if (videoPath) record.videoPath = videoPath;
    if (audioPath) record.audioPath = audioPath;
    await db.put('pendingUploads', record);
    console.log(`[BlobStorage] Updated paths for ${id}: video=${videoPath}, audio=${audioPath}`);
  }
}

/**
 * Get the stored paths for a pending upload
 */
export async function getBlobPaths(id: string): Promise<{ videoPath?: string; audioPath?: string } | null> {
  const db = await getDB();
  const record = await db.get('pendingUploads', id);

  if (record) {
    return {
      videoPath: record.videoPath,
      audioPath: record.audioPath,
    };
  }
  return null;
}

/**
 * Delete blobs from IndexedDB - ONLY after successful upload AND database save
 * This is the LAST thing to do after confirming everything is saved server-side
 */
export async function deleteBlobFromIndexedDB(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('pendingUploads', id);
  console.log(`[BlobStorage] Deleted blob record ${id}`);
}

/**
 * Delete blobs by question ID
 */
export async function deleteBlobsByQuestionId(questionId: string): Promise<void> {
  const db = await getDB();
  const records = await db.getAllFromIndex('pendingUploads', 'by-question', questionId);
  
  for (const record of records) {
    await db.delete('pendingUploads', record.id);
  }
  console.log(`[BlobStorage] Deleted ${records.length} records for question ${questionId}`);
}

/**
 * Get a specific pending upload by ID
 */
export async function getPendingUpload(id: string): Promise<PendingUpload | undefined> {
  const db = await getDB();
  return db.get('pendingUploads', id);
}

/**
 * Get all pending uploads (for recovery on app load)
 * Returns uploads that haven't been fully saved yet
 */
export async function getAllPendingUploads(): Promise<PendingUpload[]> {
  const db = await getDB();
  const all = await db.getAll('pendingUploads');
  
  // Return uploads that are pending or uploading (need retry)
  // Exclude 'saved' as those should have been deleted
  return all.filter(record => 
    record.status === 'pending' || 
    record.status === 'uploading' ||
    record.status === 'uploaded'
  );
}

/**
 * Get pending uploads for a specific test response
 */
export async function getPendingUploadsByTestResponse(
  testResponseId: string
): Promise<PendingUpload[]> {
  const db = await getDB();
  return db.getAllFromIndex('pendingUploads', 'by-test-response', testResponseId);
}

/**
 * Clean up old pending uploads (older than 24 hours)
 * Call this periodically to prevent IndexedDB bloat
 */
export async function cleanupOldPendingUploads(): Promise<number> {
  const db = await getDB();
  const all = await db.getAll('pendingUploads');
  const cutoff = Date.now() - 24 * 60 * 60 * 1000; // 24 hours
  
  let deleted = 0;
  for (const record of all) {
    if (record.createdAt < cutoff) {
      await db.delete('pendingUploads', record.id);
      deleted++;
    }
  }
  
  if (deleted > 0) {
    console.log(`[BlobStorage] Cleaned up ${deleted} old pending uploads`);
  }
  
  return deleted;
}

/**
 * Get total count of pending uploads
 */
export async function getPendingUploadCount(): Promise<number> {
  const db = await getDB();
  const all = await db.getAll('pendingUploads');
  return all.filter(r => r.status !== 'saved').length;
}

/**
 * Check if there are any pending uploads for a question
 */
export async function hasPendingUploadForQuestion(questionId: string): Promise<boolean> {
  const db = await getDB();
  const records = await db.getAllFromIndex('pendingUploads', 'by-question', questionId);
  return records.some(r => r.status !== 'saved');
}
